'use client';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { LocationLeadResponse } from '@/app/api/leads/locations/types';
import {
  AsideContainer,
  MainContainer,
} from '@/components/page-with-aside-layout';
import PrimaryHeading from '@/components/typography/PrimaryHeading';
import { useRouter } from '@/hooks/useRouter';
import { ScreenSize, useScreenSize } from '@/hooks/useScreenSize';
import { useToastError } from '@/hooks/useToastError';
import { usePathname } from '@/i18n/navigation';
import { FormTypes } from '@/lib/forms/types';
import {
  createLocationApplicationLeadDto,
  isCalendlyPreLoadable,
} from '@/lib/leads/leads';
import {
  LocationDto,
  LocationPhases,
  SimpleLocationDto,
} from '@/lib/locations/dto';
import * as gtm from '@/utils/gtm';
import { HttpMethods, tryFetchToJson } from '@/utils/http-request';
import { shallowEqual } from '@/utils/object-utils';
import {
  getAllParamsFromSearchParams,
  stringifyUrl,
} from '@/utils/stringify-url';

import LocationSidebar from '../../_components/LocationSidebar';
import {
  ApplicationFormData,
  LocationFormActions,
  LocationFormSteps,
  TourType,
} from './types';
import { getAction, getAvailableTourTypes, getDefaultTourType } from './utils';

import clsx from 'clsx';

const CalendlyModal = dynamic(() => import('@/components/CalendlyModal'), {
  ssr: false,
});
const ApplicationForm = dynamic(() => import('./ApplicationForm'), {
  ssr: false,
});
const CalendlyForm = dynamic(() => import('./CalendlyForm'), { ssr: false });
const Success = dynamic(() => import('./Success').then((mod) => mod.Success), {
  ssr: false,
});

export type LocationFormPageProps = {
  location?: LocationDto;
  allLocations?: SimpleLocationDto[];
};

export function LocationFormPage({
  location,
  allLocations,
}: LocationFormPageProps) {
  const t = useTranslations('location-form');
  const { showToast, hideToast } = useToastError();
  const router = useRouter();
  const pathname = usePathname();
  const widthWindow = useScreenSize();
  const searchParams = useSearchParams();

  const [action, setAction] = useState(
    location ? getAction(location) : LocationFormActions.General
  );
  const [currentStep, setCurrentStep] = useState(LocationFormSteps.Application);
  const [selectedLocation, setSelectedLocation] = useState<
    SimpleLocationDto | LocationDto | null
  >(location ?? null);
  const [form, setForm] = useState<ApplicationFormData>();
  const [isLoading, setIsLoading] = useState(false);
  const availableTourTypes =
    selectedLocation && getAvailableTourTypes(selectedLocation);
  const [selectedTourType, setSelectedTourType] = useState(
    getDefaultTourType(availableTourTypes, action)
  );
  const calendlyLink = availableTourTypes?.find(
    (tour) => tour.type === selectedTourType
  )?.calendlyLink;
  const canLoadCalendly = !!selectedLocation && !!form && !!calendlyLink;

  const [isCalendlySkipped, setIsCalendlySkipped] = useState(false);
  const [showCalendlyModal, setShowCalendlyModal] = useState(false);

  const showSuccessStep =
    currentStep === LocationFormSteps.Success && !!selectedLocation && !!form;

  const titleForm = location
    ? t(`${action}.title`, {
        locationName: location.name,
      })
    : t('general.title');

  const descriptionForm =
    selectedTourType == TourType.IN_PERSON &&
    location?.salesOfficeAddress != null &&
    currentStep !== LocationFormSteps.Application
      ? t('description-sales-office-open', {
          address: location?.salesOfficeAddress,
        })
      : t(`${action}.description`);

  const handleSubmitApplicationForm = async (form: ApplicationFormData) => {
    setIsLoading(true);

    await tryFetchToJson<LocationLeadResponse>({
      fetchParams: {
        input: '/api/leads/locations',
        init: {
          method: HttpMethods.Post,
          body: JSON.stringify(createLocationApplicationLeadDto(form)),
        },
      },
      onSuccess: (response) => {
        setIsLoading(false);
        hideToast();
        gtm.pushEvent('formSubmissionSuccess');

        if (
          response.isFlaggedAsBot ||
          selectedLocation?.phase === LocationPhases.Signed ||
          !calendlyLink
        ) {
          goToSuccessPage(form);
          return;
        }
        window.scroll(0, 0);

        if (widthWindow > ScreenSize.MD) {
          setCurrentStep(LocationFormSteps.Calendly);
        } else {
          setShowCalendlyModal(true);
        }
      },
      onFunctionalError: () => showToast(),
      onTechnicalError: () => showToast(),
    });
  };

  const goToSuccessPage = (form: ApplicationFormData) => {
    setCurrentStep(LocationFormSteps.Success);

    if (showCalendlyModal) {
      setShowCalendlyModal(false);
    }

    window.scroll(0, 0);

    gtm.pushEvent('form_process_complete', {
      form_type: FormTypes.LOCATION_APPLICATION,
      customer_email: form.email,
      customer_first_name: form.firstName,
      customer_last_name: form.lastName,
    });
  };

  const handleCloseCalendlyModal = (form: ApplicationFormData) => {
    setIsCalendlySkipped(true);
    goToSuccessPage(form);
  };

  useEffect(() => {
    const updateQueryParams = () => {
      if (!location && selectedLocation?.slug) {
        const allParams = getAllParamsFromSearchParams(searchParams);
        const newUrl = stringifyUrl(pathname, {
          ...allParams,
          loc: selectedLocation?.slug,
        });
        router.replace(newUrl);
      }
    };

    const newAction = selectedLocation
      ? getAction(selectedLocation)
      : LocationFormActions.General;
    setAction(newAction);
    setSelectedTourType(getDefaultTourType(availableTourTypes, newAction));

    updateQueryParams();
  }, [selectedLocation]);

  useEffect(() => {
    setIsCalendlySkipped(!canLoadCalendly);
  }, [canLoadCalendly]);

  const handleLocationChange = (sfId: string) => {
    const selectedLoc = allLocations?.find((x) => x.sfId === sfId);
    if (!selectedLoc) return;
    setSelectedLocation(selectedLoc);
  };

  const handleFormChange = (formChanged: ApplicationFormData) => {
    if (
      isCalendlyPreLoadable(formChanged) &&
      (!form || !shallowEqual(form, formChanged))
    ) {
      setForm(formChanged);
    }
  };

  return (
    <>
      <MainContainer>
        {currentStep !== LocationFormSteps.Success && (
          <>
            <PrimaryHeading className="mb-2 max-md:text-[28px]">
              {titleForm}
            </PrimaryHeading>
            <p className="mb-6">{descriptionForm}</p>
          </>
        )}

        {currentStep === LocationFormSteps.Application && (
          <ApplicationForm
            showLocationSelection={!location}
            allLocations={allLocations}
            location={selectedLocation}
            selectedTourType={selectedTourType}
            isLoading={isLoading}
            onTourTypeChange={setSelectedTourType}
            onSubmit={handleSubmitApplicationForm}
            onLocationChange={handleLocationChange}
            onChange={handleFormChange}
            availableTourTypes={availableTourTypes}
          />
        )}

        {widthWindow > ScreenSize.MD && canLoadCalendly && (
          <div
            className={clsx(
              currentStep !== LocationFormSteps.Calendly && 'hidden'
            )}
          >
            <CalendlyForm
              link={calendlyLink}
              onSubmit={() => goToSuccessPage(form)}
              company={form.company}
              email={form.email}
              firstName={form.firstName}
              lastName={form.lastName}
              phone={form.phone}
            />
          </div>
        )}
        {widthWindow <= ScreenSize.MD && canLoadCalendly && (
          <CalendlyModal
            link={calendlyLink}
            company={form.company}
            email={form.email}
            firstName={form.firstName}
            lastName={form.lastName}
            phone={form.phone}
            opened={showCalendlyModal}
            onClose={() => handleCloseCalendlyModal(form)}
            onSubmit={() => goToSuccessPage(form)}
          />
        )}

        {showSuccessStep && (
          <Success
            firstName={form.firstName}
            email={form.email}
            locationPhase={selectedLocation.phase}
            tourType={selectedTourType}
            isCalendlySkipped={isCalendlySkipped}
            salesOfficeAddress={selectedLocation.salesOfficeAddress}
          />
        )}
      </MainContainer>
      <AsideContainer className="xl:w-[40%]">
        <LocationSidebar
          location={
            location && {
              phase: location.phase,
              phone: location.phone,
              country: location.country,
            }
          }
        />
      </AsideContainer>
    </>
  );
}
